import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarIcon, KeyRound } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { useDateFormatter } from "@/hooks/useUserPreferences";
import { authClient } from "@/lib/auth";
import EnterButton from "../buttons/EnterButton";

const createApiKeySchema = z
    .object({
        name: z
            .string()
            .transform((value) => value.trim())
            .pipe(z.string().min(1, "Name is required")),
        hasExpiry: z.boolean(),
        expiresAt: z.date().nullable(),
    })
    .superRefine((data, ctx) => {
        if (!data.hasExpiry) return;
        if (!data.expiresAt) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["expiresAt"],
                message: "Pick an expiry date",
            });
        } else if (data.expiresAt.getTime() <= Date.now()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["expiresAt"],
                message: "Expiry date must be in the future",
            });
        }
    });

type CreateApiKeyFormData = z.infer<typeof createApiKeySchema>;

interface CreateApiKeyDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onCreated: (created: { id: string; key: string }) => void;
}

export default function CreateApiKeyDialog({ open, onOpenChange, onCreated }: CreateApiKeyDialogProps) {
    const queryClient = useQueryClient();
    const { formatDate } = useDateFormatter();
    const [datePickerOpen, setDatePickerOpen] = useState(false);

    const { data: activeOrganization } = authClient.useActiveOrganization();

    const form = useForm<CreateApiKeyFormData>({
        resolver: zodResolver(createApiKeySchema),
        defaultValues: {
            name: "",
            hasExpiry: false,
            expiresAt: null,
        },
    });

    const hasExpiry = form.watch("hasExpiry");

    const createMutation = useMutation({
        mutationFn: async (input: { name: string; expiresIn: number | null }) => {
            if (!activeOrganization?.id) {
                throw new Error("No active organization");
            }
            const result = await authClient.apiKey.create({
                name: input.name,
                expiresIn: input.expiresIn,
                prefix: "orsane_",
                metadata: { organizationId: activeOrganization.id },
            });
            if (result.error) throw new Error(result.error.message);
            return result.data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ["api-keys", activeOrganization?.id] });
            if (data?.id && data?.key) {
                onCreated({ id: data.id, key: data.key });
            }
            form.reset();
            onOpenChange(false);
        },
        onError: (error) => {
            toast.error("Failed to create API key", { description: error.message });
        },
    });

    const onSubmit = (data: CreateApiKeyFormData) => {
        const expiresIn =
            data.hasExpiry && data.expiresAt ? Math.floor((data.expiresAt.getTime() - Date.now()) / 1000) : null;
        createMutation.mutate({ name: data.name, expiresIn });
    };

    const handleOpenChange = (newOpen: boolean) => {
        if (!newOpen) form.reset();
        onOpenChange(newOpen);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-[480px] gap-6 rounded-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-lg font-medium">
                        <KeyRound className="size-4" />
                        Create API key
                    </DialogTitle>
                    <DialogDescription>
                        The full key will be shown once after creation. Copy it then as you won't see it again.
                        {activeOrganization?.name && (
                            <>
                                {" "}
                                This key will be scoped to{" "}
                                <span className="font-medium text-foreground">{activeOrganization.name}</span>.
                            </>
                        )}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
                    <Controller
                        control={form.control}
                        name="name"
                        render={({ field, fieldState }) => (
                            <Field>
                                <FieldLabel htmlFor="api-key-name">Name</FieldLabel>
                                <Input
                                    id="api-key-name"
                                    placeholder="e.g. Zapier integration"
                                    data-1p-ignore
                                    autoComplete="off"
                                    autoFocus
                                    disabled={createMutation.isPending}
                                    {...field}
                                />
                                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                            </Field>
                        )}
                    />

                    <Controller
                        control={form.control}
                        name="expiresAt"
                        render={({ field, fieldState }) => (
                            <Field>
                                <div className="flex items-center gap-2">
                                    <Controller
                                        control={form.control}
                                        name="hasExpiry"
                                        render={({ field: switchField }) => (
                                            <Switch
                                                id="api-key-has-expiry"
                                                checked={switchField.value}
                                                onCheckedChange={(checked) => {
                                                    switchField.onChange(checked);
                                                    if (!checked) {
                                                        field.onChange(null);
                                                        form.clearErrors("expiresAt");
                                                    }
                                                }}
                                                disabled={createMutation.isPending}
                                            />
                                        )}
                                    />
                                    <Label htmlFor="api-key-has-expiry">Set an expiration date</Label>
                                </div>
                                {hasExpiry && (
                                    <div>
                                        <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                                            <PopoverTrigger
                                                render={
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        disabled={createMutation.isPending}
                                                    >
                                                        <CalendarIcon />
                                                        {field.value ? formatDate(field.value) : "Pick a date"}
                                                    </Button>
                                                }
                                            />
                                            <PopoverContent align="start" className="p-0 w-auto">
                                                <Calendar
                                                    mode="single"
                                                    selected={field.value ?? undefined}
                                                    defaultMonth={field.value ?? undefined}
                                                    disabled={(date) => date <= new Date()}
                                                    onSelect={(date) => {
                                                        field.onChange(date ?? null);
                                                        setDatePickerOpen(false);
                                                    }}
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                )}
                                {hasExpiry && fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                            </Field>
                        )}
                    />

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenChange(false)}
                            disabled={createMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <EnterButton type="submit" disabled={createMutation.isPending}>
                            {createMutation.isPending ? "Creating..." : "Create"}
                        </EnterButton>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
